import React, { useState, useEffect } from 'react';
import { ThemeProvider, CssBaseline, Tabs, Tab, Box, createTheme, FormControl, InputLabel, Select, MenuItem, TextField, Button, Chip, FormControlLabel, Checkbox, Paper } from '@mui/material';
import styled from 'styled-components';
import MarketingBrief from './components/MarketingBrief';
import ChatInterface from './components/ChatInterface';
import TemplateEditor from './components/TemplateEditor';
import { BriefFieldDefinition, BRIEF_FIELDS, BriefData } from './types/briefTypes';
import { AIPromptConfig } from './types/promptTypes';
import { ConversationConfig } from './types/conversationTypes';
import { updateAIPrompts } from './services/openai';
import { loadTemplate } from './services/firebase';
import CompetitorSearch from './components/CompetitorSearch';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { Search as SearchIcon } from '@mui/icons-material';

const darkTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb',
    },
    secondary: {
      main: '#10b981',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
  },
});

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: #f9fafb;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background-color: #ffffff;
  padding: 1rem 2rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h1`
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
`;

const TabsContainer = styled.div`
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
`;

const MainContent = styled.main`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: calc(100vh - 120px);
`;

const SplitSection = styled.div`
  display: flex;
  height: 50%;
  min-height: 600px;
  border-bottom: 1px solid #e5e7eb;
`;

const Column = styled.div<{ side: 'left' | 'right' }>`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  ${props => props.side === 'left' ? 'border-right: 1px solid #e5e7eb;' : ''}
  height: 100%;
  max-width: 50%;
`;

const FullWidthSection = styled.div`
  padding: 2rem;
  overflow-y: auto;
  height: 50%;
  background: #ffffff;
`;

const CompetitorSearchContainer = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
`;

const SearchFiltersContainer = styled(Paper)`
  padding: 1rem;
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: flex-start;
`;

const SearchButton = styled(Button)`
  height: 56px;
  min-width: 120px;
`;

interface SearchFilters {
  startDate: Date | null;
  endDate: Date | null;
  contentTypes: string[];
  sortBy: 'date' | 'relevance';
  competitors: string[];
}

const App: React.FC = () => {
  const [briefData, setBriefData] = useState<BriefData>({
    campaignName: '',
    campaignType: '',
    primaryObjective: '',
    eventObjective: '',
    eventDate: '',
    targetAudience: '',
    budget: '',
    timeline: '',
    keyMessages: '',
    channels: '',
    activityKPIs: '',
    businessKPIs: '',
    creativeAssets: '',
    additionalNotes: ''
  });

  const [currentTab, setCurrentTab] = useState(0);
  const [competitorSearchQuery, setCompetitorSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');

  const [fields, setFields] = useState<BriefFieldDefinition[]>(BRIEF_FIELDS);
  const [aiPrompts, setAIPrompts] = useState<AIPromptConfig>({
    targetAudience: `You are a social media advertising expert. Based on the campaign context, generate a target audience description in social media targeting format.
Format the response in clear sections:

Demographics:
- Age Range: [specific range]
- Gender: [if applicable]
- Location: [regions/countries]
- Language: [primary languages]
- Education Level: [if relevant]
- Job Titles: [specific roles]
- Company Size: [if B2B]
- Industry: [specific sectors]

Interests:
- Professional Interests: [list]
- Topics: [list]
- Related Fields: [list]

Behaviors:
- Online Platforms: [list]
- Content Consumption: [patterns]
- Professional Activities: [list]

Format as a structured, bullet-point list that could be directly used for social media targeting.`,
    informationExtraction: `Jesteś asystentem AI, który wydobywa informacje o kampanii marketingowej z wiadomości użytkownika.
NAJPIERW wydobądź wszystkie faktyczne informacje, nawet jeśli wiadomość zawiera pytania.

Przykładowe wejście: "Mam webinar o AI Act w UE. Będzie 25 lutego. Nie wiem kto jest grupą docelową, możesz coś zasugerować?"

Przykładowe wyjście:
{
  "campaignName": "Webinar AI Act 2025",
  "campaignType": "Webinar",
  "eventDate": "25 lutego 2025",
  "keyMessages": "AI Act w UE",
  "objective": "Promocja Raportu",
  "channels": ["GlobalMeet", "YouTube", "LinkedIn Live"],
  "kpis": [
    "Główne KPI - Liczba Pobrań Raportu",
    "Główne KPI - Wskaźnik Konwersji Pobrań",
    "Webinar KPI - Liczba Rejestracji",
    "Webinar KPI - Współczynnik Uczestnictwa"
  ]
}`,
    recommendations: `You are an experienced marketing strategist. Based on the campaign brief details, suggest 3-5 complementary actions or opportunities that could enhance the campaign's success. Consider:

1. Additional channels or touchpoints
2. Pre and post-event activities
3. Content opportunities
4. Audience engagement strategies
5. Lead nurturing possibilities
6. Measurement and follow-up tactics

Format each suggestion as an actionable item starting with a verb (e.g., "Create", "Launch", "Develop").
Make suggestions specific to the campaign context.`
  });

  const [conversationConfig, setConversationConfig] = useState<ConversationConfig>({
    initialGreeting: `Cześć! Jestem asystentem AI i pomogę Ci stworzyć brief kampanii marketingowej. Przeprowadzę Cię przez cały proces. Zacznijmy od omówienia Twojej kampanii - opowiedz mi o niej?`,
    fieldPrompts: {},
    followUpStyle: `When asking follow-up questions:
- Be concise and clear
- One question at a time
- Acknowledge previous information
- Use context from earlier responses
- Maintain a conversational tone`,
    responseStyle: `When responding to user input:
- Be professional yet friendly
- Acknowledge user's input
- Provide relevant suggestions
- Stay focused on the campaign brief
- Guide user through the process`,
    prerequisites: []
  });

  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    startDate: null,
    endDate: null,
    contentTypes: [],
    sortBy: 'date',
    competitors: ['deloitte', 'ey', 'pwc', 'mckinsey', 'kpmg', 'accenture']
  });

  const updateBriefData = (newData: Partial<BriefData>) => {
    setBriefData(prev => ({ ...prev, ...newData }));
  };

  // Load saved template on app start
  useEffect(() => {
    const loadSavedTemplate = async () => {
      const userId = 'default-user'; // In a real app, this would come from authentication
      const savedTemplate = await loadTemplate(userId);
      
      if (savedTemplate) {
        setFields(savedTemplate.fields);
        setAIPrompts(savedTemplate.aiPrompts);
        setConversationConfig(savedTemplate.conversationConfig);
      }
    };

    loadSavedTemplate();
  }, []); // Run once on app start

  // Update OpenAI service when prompts change
  useEffect(() => {
    updateAIPrompts(aiPrompts);
  }, [aiPrompts]);

  const handleSearch = () => {
    setActiveSearchQuery(competitorSearchQuery);
  };

  const contentTypeOptions = [
    { value: 'raport', label: 'Raport' },
    { value: 'podcast', label: 'Podcast' },
    { value: 'wydarzenie', label: 'Wydarzenie / Webinar' }
  ];

  const competitorOptions = [
    { value: 'deloitte', label: 'Deloitte' },
    { value: 'ey', label: 'EY' },
    { value: 'pwc', label: 'PwC' },
    { value: 'mckinsey', label: 'McKinsey' },
    { value: 'kpmg', label: 'KPMG' },
    { value: 'accenture', label: 'Accenture' }
  ];

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AppContainer>
        <Header>
          <Title>Marketing Brief Generator</Title>
        </Header>

        <TabsContainer>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
            <Tab label="Brief Creator" />
            <Tab label="Template Editor" />
            <Tab label="Competitor Search" />
          </Tabs>
        </TabsContainer>

        {currentTab === 0 ? (
          <MainContent>
            <SplitSection>
              <Column side="left">
                <MarketingBrief briefData={briefData} />
              </Column>
              <Column side="right">
                <ChatInterface 
                  onUpdateBrief={updateBriefData}
                  briefData={briefData}
                  conversationConfig={conversationConfig}
                />
              </Column>
            </SplitSection>

            {(briefData.campaignName?.trim() || briefData.keyMessages?.trim()) && (
              <FullWidthSection>
                <CompetitorSearch 
                  searchQuery={briefData.keyMessages?.trim() || ''}
                  filters={{
                    startDate: null,
                    endDate: null,
                    contentTypes: [] as string[],
                    sortBy: 'date',
                    competitors: ['deloitte', 'ey', 'pwc', 'mckinsey', 'kpmg']
                  }}
                />
              </FullWidthSection>
            )}
          </MainContent>
        ) : currentTab === 1 ? (
          <Box sx={{ p: 3, height: 'calc(100vh - 180px)', overflow: 'auto' }}>
            <TemplateEditor
              fields={fields}
              onFieldsChange={setFields}
              aiPrompts={aiPrompts}
              onAIPromptsChange={setAIPrompts}
              conversationConfig={conversationConfig}
              onConversationConfigChange={setConversationConfig}
            />
          </Box>
        ) : (
          <CompetitorSearchContainer>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <SearchFiltersContainer>
                <FiltersRow>
                  <TextField
                    fullWidth
                    placeholder="Enter your topic to search across competitor sites..."
                    value={competitorSearchQuery}
                    onChange={(e) => setCompetitorSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <SearchButton
                    variant="contained"
                    onClick={handleSearch}
                    startIcon={<SearchIcon />}
                  >
                    Search
                  </SearchButton>
                </FiltersRow>

                <FiltersRow>
                  <DatePicker
                    label="From Date"
                    value={searchFilters.startDate}
                    onChange={(date: Date | null) => setSearchFilters(prev => ({ ...prev, startDate: date }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DatePicker
                    label="To Date"
                    value={searchFilters.endDate}
                    onChange={(date: Date | null) => setSearchFilters(prev => ({ ...prev, endDate: date }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={searchFilters.sortBy}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, sortBy: e.target.value as 'date' | 'relevance' }))}
                      label="Sort By"
                    >
                      <MenuItem value="date">Most Recent</MenuItem>
                      <MenuItem value="relevance">Most Relevant</MenuItem>
                    </Select>
                  </FormControl>
                </FiltersRow>

                <FiltersRow>
                  <Box>
                    <InputLabel sx={{ marginBottom: 1 }}>Content Types</InputLabel>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {contentTypeOptions.map(type => (
                        <Chip
                          key={type.value}
                          label={type.label}
                          onClick={() => {
                            setSearchFilters(prev => ({
                              ...prev,
                              contentTypes: prev.contentTypes.includes(type.value)
                                ? prev.contentTypes.filter(t => t !== type.value)
                                : [...prev.contentTypes, type.value]
                            }));
                          }}
                          color={searchFilters.contentTypes.includes(type.value) ? "primary" : "default"}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Box>
                </FiltersRow>

                <FiltersRow>
                  <Box>
                    <InputLabel sx={{ marginBottom: 1 }}>Competitors</InputLabel>
                    <Box display="flex" gap={1}>
                      {competitorOptions.map(competitor => (
                        <FormControlLabel
                          key={competitor.value}
                          control={
                            <Checkbox
                              checked={searchFilters.competitors.includes(competitor.value)}
                              onChange={(e) => {
                                setSearchFilters(prev => ({
                                  ...prev,
                                  competitors: e.target.checked
                                    ? [...prev.competitors, competitor.value]
                                    : prev.competitors.filter(c => c !== competitor.value)
                                }));
                              }}
                            />
                          }
                          label={competitor.label}
                        />
                      ))}
                    </Box>
                  </Box>
                </FiltersRow>
              </SearchFiltersContainer>

              <CompetitorSearch 
                searchQuery={activeSearchQuery}
                filters={searchFilters}
              />
            </LocalizationProvider>
          </CompetitorSearchContainer>
        )}
      </AppContainer>
    </ThemeProvider>
  );
};

export default App; 